declare global {
  interface Navigator {
    userLanguage?: string;
    browserLanguage?: string;
  }
}

/** @hidden */
const FACEBOOK_SUPPORTED_LOCALES = [
  'af_ZA', // Afrikaans
  'ak_GH', // Akan
  'am_ET', // Amharic
  'ar_AR', // Arabic
  'as_IN', // Assamese
  'ay_BO', // Aymara
  'az_AZ', // Azerbaijani
  'be_BY', // Belarusian
  'bg_BG', // Bulgarian
  'bn_IN', // Bengali
  'br_FR', // Breton
  'bs_BA', // Bosnian
  'ca_ES', // Catalan
  'cb_IQ', // Sorani Kurdish
  'ck_US', // Cherokee
  'co_FR', // Corsican
  'cs_CZ', // Czech
  'cx_PH', // Cebuano
  'cy_GB', // Welsh
  'de_DE', // German
  'da_DK', // Danish
  'el_GR', // Greek
  'en_GB', // English (UK)
  'en_IN', // English (India)
  'en_PI', // English (Pirate)
  'en_UD', // English (Upside Down)
  'en_US', // English (US)
  'eo_EO', // Esperanto
  'es_LA', // Spanish
  'es_CL', // Spanish (Chile)
  'es_CO', // Spanish (Colombia)
  'es_ES', // Spanish (Spain)
  'es_MX', // Spanish (Mexico)
  'es_VE', // Spanish (Venezuela)
  'et_EE', // Estonian
  'eu_ES', // Basque
  'fa_IR', // Persian
  'fb_LT', // Leet Speak
  'ff_NG', // Fulah
  'fi_FI', // Finnish
  'fo_FO', // Faroese
  'fr_FR', // French (France)
  'fr_CA', // French (Canada)
  'fy_NL', // Frisian
  'ga_IE', // Irish
  'gl_ES', // Galician
  'gn_PY', // Guarani
  'gu_IN', // Gujarati
  'gx_GR', // Classical Greek
  'ha_NG', // Hausa
  'he_IL', // Hebrew
  'hi_IN', // Hindi
  'hr_HR', // Croatian
  'hu_HU', // Hungarian
  'hy_AM', // Armenian
  'id_ID', // Indonesian
  'ig_NG', // Igbo
  'is_IS', // Icelandic
  'it_IT', // Italian
  'ja_JP', // Japanese
  'ja_KS', // Japanese (Kansai)
  'jv_ID', // Javanese
  'ka_GE', // Georgian
  'kk_KZ', // Kazakh
  'km_KH', // Khmer
  'kn_IN', // Kannada
  'ko_KR', // Korean
  'ku_TR', // Kurdish (Kurmanji)
  'la_VA', // Latin
  'lg_UG', // Ganda
  'li_NL', // Limburgish
  'ln_CD', // Lingala
  'lo_LA', // Lao
  'lt_LT', // Lithuanian
  'lv_LV', // Latvian
  'mg_MG', // Malagasy
  'mk_MK', // Macedonian
  'ml_IN', // Malayalam
  'mn_MN', // Mongolian
  'mr_IN', // Marathi
  'ms_MY', // Malay
  'mt_MT', // Maltese
  'my_MM', // Burmese
  'nb_NO', // Norwegian (bokmal)
  'nd_ZW', // Ndebele
  'ne_NP', // Nepali
  'nl_BE', // Dutch (Belgi?)
  'nl_NL', // Dutch
  'nn_NO', // Norwegian (nynorsk)
  'ny_MW', // Chewa
  'or_IN', // Oriya
  'pa_IN', // Punjabi
  'pl_PL', // Polish
  'ps_AF', // Pashto
  'pt_BR', // Portuguese (Brazil)
  'pt_PT', // Portuguese (Portugal)
  'qu_PE', // Quechua
  'rm_CH', // Romansh
  'ro_RO', // Romanian
  'ru_RU', // Russian
  'rw_RW', // Kinyarwanda
  'sa_IN', // Sanskrit
  'sc_IT', // Sardinian
  'se_NO', // Northern S?mi
  'si_LK', // Sinhala
  'sk_SK', // Slovak
  'sl_SI', // Slovenian
  'sn_ZW', // Shona
  'so_SO', // Somali
  'sq_AL', // Albanian
  'sr_RS', // Serbian
  'sv_SE', // Swedish
  'sw_KE', // Swahili
  'sy_SY', // Syriac
  'sz_PL', // Silesian
  'ta_IN', // Tamil
  'te_IN', // Telugu
  'tg_TJ', // Tajik
  'th_TH', // Thai
  'tk_TM', // Turkmen
  'tl_PH', // Filipino
  'tl_ST', // Klingon
  'tr_TR', // Turkish
  'tt_RU', // Tatar
  'tz_MA', // Tamazight
  'uk_UA', // Ukrainian
  'ur_PK', // Urdu
  'uz_UZ', // Uzbek
  'vi_VN', // Vietnamese
  'wo_SN', // Wolof
  'xh_ZA', // Xhosa
  'yi_DE', // Yiddish
  'yo_NG', // Yoruba
  'zh_CN', // Simplified Chinese (China)
  'zh_HK', // Traditional Chinese (Hong Kong)
  'zh_TW', // Traditional Chinese (Taiwan)
  'zu_ZA', // Zulu
  'zz_TR' // Zazaki
];

// adapted from https://github.com/wix/facebook-locales/blob/master/src/FacebookLocales.js
/** @hidden */
const facebookVirtualLocales: { [index: string]: string } = {
  // Spanish (Latin America)... overriding the non-existent Spanish (Laos)
  es_AR: 'es_LA', // Argentina
  es_PE: 'es_LA', // Peru
  es_EC: 'es_LA', // Ecuador,
  es_GT: 'es_LA', // Guatemala
  es_CU: 'es_LA', // Cuba
  es_BO: 'es_LA', // Bolivia
  es_DO: 'es_LA', // Dominican Republic
  es_HN: 'es_LA', // Honduras
  es_PY: 'es_LA', // Paraguay
  es_SV: 'es_LA', // El Salvador
  es_NI: 'es_LA', // Nicaragua
  es_CR: 'es_LA', // Costa Rica
  es_PR: 'es_LA', // Puerto Rico
  es_PA: 'es_LA', // Panama
  es_UY: 'es_LA', // Uruguay
  es_GQ: 'es_LA', // Equatorial Guinea,
  // Arabic... overriding the non-existent Arabic (Argentina)
  ar_DZ: 'ar_AR', // Algeria
  ar_BH: 'ar_AR', // Bahrain
  ar_TD: 'ar_AR', // Chad
  ar_KM: 'ar_AR', // Comoros
  ar_DJ: 'ar_AR', // Djibouti
  ar_EG: 'ar_AR', // Egypt
  ar_ER: 'ar_AR', // Eritrea
  ar_GM: 'ar_AR', // The Gambia
  ar_IQ: 'ar_AR', // Iraq
  ar_IL: 'ar_AR', // Israel
  ar_JO: 'ar_AR', // Jordan
  ar_KW: 'ar_AR', // Kuwait
  ar_LB: 'ar_AR', // Lebanon
  ar_LY: 'ar_AR', // Libya
  ar_MR: 'ar_AR', // Mauritania
  ar_MA: 'ar_AR', // Morocco
  ar_OM: 'ar_AR', // Oman
  ar_PS: 'ar_AR', // State of Palestine
  ar_QA: 'ar_AR', // Qatar
  ar_SA: 'ar_AR', // Saudi Arabia
  ar_SO: 'ar_AR', // Somalia
  ar_SD: 'ar_AR', // Sudan
  ar_SY: 'ar_AR', // Syria
  ar_TN: 'ar_AR', // Tunisia
  ar_AE: 'ar_AR', // United Arab Emirates
  ar_YE: 'ar_AR' // Yemen
};

/** @hidden */
export const bestFacebookLocaleFor = (locale: string): string => {
  const cleanedLocale = locale.replace('-', '_');

  // standard FB supported locale
  if (FACEBOOK_SUPPORTED_LOCALES.indexOf(cleanedLocale) > -1) {
    return cleanedLocale;
  }

  // non-standard FB locale
  if (facebookVirtualLocales[cleanedLocale]) {
    return facebookVirtualLocales[cleanedLocale];
  }

  // unsupported locale, attempt to parse and return closest match
  const lang = cleanedLocale.substring(0, 2);
  const supportedLang = FACEBOOK_SUPPORTED_LOCALES.find(a => a.substring(0, 2) === lang);
  if (supportedLang) {
    return supportedLang;
  }

  // unsupported locale, fallback to en_US;
  return 'en_US';
};

/** @hidden */
export const getBrowserLocale = (): string => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  } else {
    return navigator.userLanguage || navigator.language || navigator.browserLanguage || 'en_US';
  }
};
